import { useEffect, useMemo, useState } from 'react';
import type { TicketAttachment, TicketMessage } from '../../types/tickets';
import { formatTicketDateTime, splitQuotedEmailHtml } from '../../lib/ticketComposeHelpers';
import {
  getAttachmentUrlMap,
  getTicketAttachmentUrl,
  regularFileAttachments,
  resolveTicketMessageHtml,
} from '../../lib/tickets';
import { IsolatedEmailHtml } from './IsolatedEmailHtml';

type Props = {
  messages: TicketMessage[];
  attachmentsByMessage: Record<string, TicketAttachment[]>;
  loading?: boolean;
  accordion?: boolean;
  onDeleteMessage?: (messageId: string) => void | Promise<void>;
  onEditNote?: (message: TicketMessage) => void;
};

function senderLabel(msg: TicketMessage): string {
  if (msg.direction === 'outbound') return 'You';
  if (msg.direction === 'note') return 'Internal note';
  if (msg.from_name) return `${msg.from_name} <${msg.from_email}>`;
  return msg.from_email || 'Customer';
}

/** Opening acknowledgement auto-sent when a ticket is created from inbound mail. */
function isOpeningAckMessage(msg: TicketMessage): boolean {
  if (msg.direction !== 'outbound') return false;
  if (msg.created_by_email.trim().toLowerCase() === 'system@ticketing') return true;
  return /your ticket has been created/i.test(msg.subject);
}

function isImageAttachment(att: TicketAttachment): boolean {
  const ct = att.content_type.toLowerCase();
  if (ct.startsWith('image/')) return true;
  return /\.(png|jpe?g|gif|webp|bmp|svg)$/i.test(att.file_name);
}

async function openAttachment(path: string) {
  const url = await getTicketAttachmentUrl(path);
  window.open(url, '_blank', 'noopener,noreferrer');
}

function AttachmentGallery({
  attachments,
  urlById,
}: {
  attachments: TicketAttachment[];
  urlById: Map<string, string>;
}) {
  if (attachments.length === 0) return null;

  const images = attachments.filter(isImageAttachment);
  const files = attachments.filter((a) => !isImageAttachment(a));

  return (
    <div className="ticket-mail__attach-block">
      <p className="ticket-mail__attach-title">
        Attachments ({attachments.length})
      </p>
      {images.length > 0 && (
        <div className="ticket-mail__image-grid">
          {images.map((att) => {
            const url = urlById.get(att.id);
            return (
              <button
                key={att.id}
                type="button"
                className="ticket-mail__image-thumb"
                onClick={() => void openAttachment(att.storage_path)}
                title={att.file_name}
              >
                {url ? (
                  <img src={url} alt={att.file_name} loading="lazy" />
                ) : (
                  <span>{att.file_name}</span>
                )}
              </button>
            );
          })}
        </div>
      )}
      {files.length > 0 && (
        <ul className="ticket-mail__attachments">
          {files.map((att) => (
            <li key={att.id}>
              <button
                type="button"
                className="ticket-mail__attachment"
                onClick={() => void openAttachment(att.storage_path)}
              >
                {att.file_name}
                <span className="ticket-mail__attachment-size">
                  {att.size_bytes > 0 ? ` (${Math.round(att.size_bytes / 1024)} KB)` : ''}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function MessageBody({
  msg,
  attachments,
  urlById,
}: {
  msg: TicketMessage;
  attachments: TicketAttachment[];
  urlById: Map<string, string>;
}) {
  const [html, setHtml] = useState(msg.body_html || msg.body_text.replace(/\n/g, '<br>'));
  const [showQuoted, setShowQuoted] = useState(false);
  const attachmentKey = useMemo(
    () => attachments.map((a) => `${a.id}:${a.storage_path}`).join('|'),
    [attachments],
  );

  // Only collapse quoted section when the message body itself changes — not when
  // inline-image resolution finishes (that was snapping "Show more" shut).
  useEffect(() => {
    setShowQuoted(false);
  }, [msg.id, msg.body_html, msg.body_text]);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const raw = msg.body_html || msg.body_text.replace(/\n/g, '<br>');
      const resolved = await resolveTicketMessageHtml(raw, attachments);
      if (cancelled) return;
      // Keep previous HTML when unchanged so animated GIFs don't restart / steal selection.
      setHtml((prev) => (prev === resolved ? prev : resolved));
    })();
    return () => {
      cancelled = true;
    };
    // attachmentKey captures identity; attachments used inside for current paths.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional stable key
  }, [msg.body_html, msg.body_text, attachmentKey]);

  const { main, quoted } = useMemo(() => splitQuotedEmailHtml(html), [html]);
  const fileAttachments = regularFileAttachments(attachments, msg.body_html || '');

  return (
    <>
      <IsolatedEmailHtml
        className="ticket-mail__body"
        html={main || html}
      />
      {quoted ? (
        <div className="ticket-mail__quoted">
          <button
            type="button"
            className="ticket-mail__quoted-toggle"
            onClick={(e) => {
              e.stopPropagation();
              setShowQuoted((v) => !v);
            }}
          >
            {showQuoted ? 'Show less' : 'Show more'}
          </button>
          {showQuoted ? (
            <IsolatedEmailHtml
              className="ticket-mail__body ticket-mail__body--quoted"
              html={quoted}
            />
          ) : null}
        </div>
      ) : null}
      <AttachmentGallery attachments={fileAttachments} urlById={urlById} />
    </>
  );
}

export function TicketMessageTimeline({
  messages,
  attachmentsByMessage,
  loading,
  accordion = true,
  onDeleteMessage,
  onEditNote,
}: Props) {
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [urlMaps, setUrlMaps] = useState<Record<string, Map<string, string>>>({});

  const ordered = useMemo(
    () =>
      [...messages].sort(
        (a, b) => new Date(b.sent_at).getTime() - new Date(a.sent_at).getTime(),
      ),
    [messages],
  );

  const ticketId = ordered[0]?.ticket_id ?? '';
  const defaultOpenId = useMemo(() => {
    const preferred = ordered.find((m) => !isOpeningAckMessage(m));
    return preferred?.id ?? ordered[0]?.id ?? '';
  }, [ordered]);

  useEffect(() => {
    setExpanded(defaultOpenId ? { [defaultOpenId]: true } : {});
  }, [ticketId, defaultOpenId]);

  function toggleMessage(msgId: string, isOpen: boolean) {
    if (accordion) {
      setExpanded(isOpen ? {} : { [msgId]: true });
      return;
    }
    setExpanded((prev) => ({ ...prev, [msgId]: !isOpen }));
  }

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const next: Record<string, Map<string, string>> = {};
      for (const msg of messages) {
        const atts = attachmentsByMessage[msg.id] ?? [];
        if (atts.length === 0) continue;
        next[msg.id] = await getAttachmentUrlMap(atts);
      }
      if (!cancelled) setUrlMaps(next);
    })();
    return () => {
      cancelled = true;
    };
  }, [messages, attachmentsByMessage]);

  const attachmentCountByMessage = useMemo(() => {
    const map: Record<string, number> = {};
    for (const msg of messages) {
      map[msg.id] = regularFileAttachments(
        attachmentsByMessage[msg.id] ?? [],
        msg.body_html || '',
      ).length;
    }
    return map;
  }, [messages, attachmentsByMessage]);

  if (ordered.length === 0) {
    if (loading) {
      return <p className="ticket-mail-list__empty">Loading messages…</p>;
    }
    return <p className="ticket-mail-list__empty">No messages yet.</p>;
  }

  return (
    <div className="ticket-mail-list">
      {ordered.map((msg) => {
        const isNote = msg.direction === 'note';
        const isAck = isOpeningAckMessage(msg);
        const isOpen = isNote || expanded[msg.id] === true;
        const attachments = attachmentsByMessage[msg.id] ?? [];
        const urlById = urlMaps[msg.id] ?? new Map<string, string>();
        const attCount = attachmentCountByMessage[msg.id] ?? 0;

        if (isNote) {
          return (
            <article key={msg.id} className="ticket-chat-note ticket-chat-note--thread">
              <div className="ticket-chat-note__bubble">
                <p className="ticket-chat-note__label">Internal note</p>
                <div className="ticket-chat-note__body">
                  <MessageBody msg={msg} attachments={attachments} urlById={urlById} />
                </div>
                <div className="ticket-chat-note__footer">
                  <time className="ticket-chat-note__meta" dateTime={msg.sent_at}>
                    {formatTicketDateTime(msg.sent_at)}
                  </time>
                  <div className="ticket-chat-note__actions">
                    {onEditNote ? (
                      <button
                        type="button"
                        className="ticket-mail__action-btn"
                        onClick={() => onEditNote(msg)}
                      >
                        Edit
                      </button>
                    ) : null}
                    {onDeleteMessage ? (
                      <button
                        type="button"
                        className="ticket-mail__action-btn ticket-mail__action-btn--danger"
                        onClick={() => {
                          if (!window.confirm('Delete this internal note?')) return;
                          void onDeleteMessage(msg.id);
                        }}
                      >
                        Delete
                      </button>
                    ) : null}
                  </div>
                </div>
              </div>
            </article>
          );
        }

        return (
          <article
            key={msg.id}
            className={`ticket-mail${isOpen ? ' ticket-mail--open' : ''} ticket-mail--${msg.direction}`}
          >
            <button
              type="button"
              className="ticket-mail__header"
              onClick={() => toggleMessage(msg.id, isOpen)}
            >
              <div className="ticket-mail__header-main">
                <span className="ticket-mail__from">
                  {senderLabel(msg)}
                  {isAck ? <span className="ticket-mail__ack-badge">Auto reply</span> : null}
                </span>
                {!isOpen && (
                  <span className="ticket-mail__preview">
                    {msg.body_text.slice(0, 140) || msg.subject}
                  </span>
                )}
              </div>
              <div className="ticket-mail__header-end">
                <time className="ticket-mail__time" dateTime={msg.sent_at}>
                  {formatTicketDateTime(msg.sent_at)}
                </time>
                {attCount > 0 ? (
                  <span className="ticket-mail__attach-badge">
                    {attCount} file{attCount === 1 ? '' : 's'}
                  </span>
                ) : null}
                {isOpen && onDeleteMessage ? (
                  <button
                    type="button"
                    className="ticket-mail__delete-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      if (
                        !window.confirm(
                          'Delete this message from the conversation? This cannot be undone.',
                        )
                      ) {
                        return;
                      }
                      void onDeleteMessage(msg.id);
                    }}
                  >
                    Delete message
                  </button>
                ) : null}
              </div>
            </button>

            {isOpen && (
              <div className="ticket-mail__content">
                <dl className="ticket-mail__meta">
                    {msg.to_emails.length > 0 && (
                      <>
                        <dt>To</dt>
                        <dd>{msg.to_emails.join(', ')}</dd>
                      </>
                    )}
                    {msg.cc_emails.length > 0 && (
                      <>
                        <dt>Cc</dt>
                        <dd>{msg.cc_emails.join(', ')}</dd>
                      </>
                    )}
                    {msg.subject && (
                      <>
                        <dt>Subject</dt>
                        <dd>{msg.subject}</dd>
                      </>
                    )}
                  </dl>
                <MessageBody msg={msg} attachments={attachments} urlById={urlById} />
              </div>
            )}
          </article>
        );
      })}
    </div>
  );
}
